import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import JobView from './components/JobView';
import { useHistory, useParams } from 'react-router-dom';
import CompanyView from '../CompanyView';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import JobGeneralPublicView from './components/JobGeneralPublicView';
import SimilarJobs from './components/SimilarJobs';
import JobSidebar from './components/JobSidebar';
import CompanySidebarInfo from './components/CompanySidebarInfo';
import { GeoLockDialog } from '../../components';
import {
  getUserLocation,
  isUserInAllowedRegion
} from '../../services/geolocationService';
import { PLAN_TYPE } from '../../config';
import usePageTitle from '../../hooks/usePageTitle';
import useStructuredData from '../../hooks/useStructuredData';
import useMetaTags from '../../hooks/useMetaTags';
import useCanonicalUrl from '../../hooks/useCanonicalUrl';
import {
  generateBreadcrumbSchema,
  generateJobPostingSchema
} from '../../helpers/structuredDataHelpers';
import { buildJobUrl } from '../../helpers/urlHelpers';
import {
  currentEnrichedJobErrorSelector,
  currentEnrichedJobLoadingSelector,
  currentEnrichedJobSelector,
  fetchEnrichedJobById
} from '../../redux/features/job/jobsSlice';

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    flexDirection: 'column',
    padding: theme.spacing(4)
  },
  backButton: {
    textTransform: 'none',
    color: theme.palette.text.primary,
    fontWeight: 500,
    padding: theme.spacing(1, 2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  backButtonBottom: {
    textTransform: 'none',
    color: theme.palette.primary.main,
    fontWeight: 600,
    padding: theme.spacing(1.5, 3),
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4]
    },
    transition: 'all 0.3s ease'
  },
  contentHeader: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(5),
    paddingBototm: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  bottomNavigation: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    gap: theme.spacing(2),
    flexDirection: 'column'
  },
  bottomNavigationText: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1)
  }
}));

const JobViewPublic = () => {
  const { id: jobId } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();

  // Get enriched job from Redux (includes job, company, and file)
  const enrichedJob = useSelector(currentEnrichedJobSelector);
  const isLoading = useSelector(currentEnrichedJobLoadingSelector);
  const error = useSelector(currentEnrichedJobErrorSelector);

  // Geo-lock state
  const [geoLockDialogOpen, setGeoLockDialogOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isGeoBlocked, setIsGeoBlocked] = useState(false);
  const [checkingLocation, setCheckingLocation] = useState(false);

  console.log('🔍 [JobViewPublic] jobId from URL:', jobId);
  console.log('🔍 [JobViewPublic] enrichedJob:', enrichedJob);
  console.log('🔍 [JobViewPublic] isLoading:', isLoading);

  // Set dynamic page title based on job data
  usePageTitle(
    enrichedJob && enrichedJob.job && enrichedJob.company
      ? `${enrichedJob.job.title} at ${enrichedJob.company.name}`
      : 'Job Details'
  );

  // Add JobPosting structured data for SEO
  const jobPostingSchema = enrichedJob && enrichedJob.job && enrichedJob.company
    ? generateJobPostingSchema(enrichedJob.job, enrichedJob.company)
    : null;
  useStructuredData(jobPostingSchema, 'job-posting-schema');

  // Add breadcrumb structured data
  const breadcrumbSchema = enrichedJob && enrichedJob.job && enrichedJob.company
    ? generateBreadcrumbSchema([
        { name: 'Home', url: 'https://remote-job.ai/' },
        { name: 'Jobs', url: 'https://remote-job.ai/jobs' },
        {
          name: enrichedJob.job.title,
          url: `https://remote-job.ai/job/${jobId}`
        }
      ])
    : null;
  useStructuredData(breadcrumbSchema, 'breadcrumb-schema');

  // Helper function to truncate text to max length
  const truncateText = (text, maxLength = 160) => {
    if (!text || typeof text !== 'string') return '';
    const plainText = text.replace(/<[^>]*>/g, ''); // Remove HTML tags
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength).trim() + '...';
  };

  // Generate Open Graph and Twitter Card meta tags
  const metaTags = useMemo(() => {
    if (!enrichedJob || !enrichedJob.job || !enrichedJob.company) return null;

    const job = enrichedJob.job;
    const company = enrichedJob.company;
    const file = enrichedJob.file || {};

    // Build SEO-friendly URL with slug
    const jobUrl = buildJobUrl(jobId, job.title, company.name);

    return {
      title: `${job.title} at ${company.name}`,
      description: truncateText(job.detail || job.description, 160),
      image: file.publicUrl || 'https://remote-job.ai/images/social-preview.jpg',
      url: `https://remote-job.ai${jobUrl}`,
      type: 'article'
    };
  }, [enrichedJob, jobId]);

  // Apply meta tags
  useMetaTags(metaTags);

  // Set canonical URL with slug
  const canonicalUrl = useMemo(() => {
    if (!enrichedJob?.job || !enrichedJob?.company) return null;
    const jobUrl = buildJobUrl(jobId, enrichedJob.job.title, enrichedJob.company.name);
    return `https://remote-job.ai${jobUrl}`;
  }, [enrichedJob, jobId]);

  useCanonicalUrl(canonicalUrl);

  // Fetch enriched job if needed
  useEffect(() => {
    if (jobId) {
      console.log(
        '🔄 [JobViewPublic] Dispatching fetchEnrichedJobById for:',
        jobId
      );
      dispatch(fetchEnrichedJobById({ jobId }));
    }
  }, [jobId, dispatch]);

  useEffect(() => {
    if (jobId) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [jobId]);

  // Check geo-lock when job is loaded
  useEffect(() => {
    const checkGeoLock = async () => {
      if (!enrichedJob || !enrichedJob.job) {
        return;
      }

      const job = enrichedJob.job;
      const plan = enrichedJob.plan;

      console.log('🔒 [GeoLock] Checking geo-lock for job:', job.id);
      console.log('🔒 [GeoLock] Job regional restrictions:', job.regionalRestrictions);
      console.log('🔒 [GeoLock] Plan data:', plan);

      // Check if job has geo-lock plan
      let hasGeoLock = false;
      
      if (plan && plan.items) {
        // Check if plan items include geo-lock plan type
        const planItems = Array.isArray(plan.items) ? plan.items : [plan.items];
        hasGeoLock = planItems.some(item => {
          // Check if item has planId matching geo-lock
          if (typeof item === 'object' && item.planId === PLAN_TYPE.GEO_LOCK) {
            return true;
          }
          // Check if item.id matches
          if (typeof item === 'object' && item.id === PLAN_TYPE.GEO_LOCK) {
            return true;
          }
          return false;
        });

        // Also check metadata for geoLockEnabled flag
        if (plan.metadata && plan.metadata.geoLockEnabled === true) {
          hasGeoLock = true;
        }
      }

      console.log('🔒 [GeoLock] Has geo-lock plan:', hasGeoLock);

      if (!hasGeoLock) {
        console.log('🔒 [GeoLock] No geo-lock, allowing access');
        return;
      }

      // Job is geo-locked, check user's location
      setCheckingLocation(true);
      console.log('🔒 [GeoLock] Detecting user location...');

      try {
        const location = await getUserLocation();
        setUserLocation(location);
        console.log('🔒 [GeoLock] User location detected:', location);

        if (location) {
          const isAllowed = isUserInAllowedRegion(
            location,
            job.regionalRestrictions
          );
          console.log('🔒 [GeoLock] Is user allowed:', isAllowed);

          if (!isAllowed) {
            setIsGeoBlocked(true);
            setGeoLockDialogOpen(true);
          }
        } else {
          // If we can't detect location, show a warning but allow access
          console.warn('🔒 [GeoLock] Could not detect location, allowing access');
        }
      } catch (err) {
        console.error('🔒 [GeoLock] Error checking geo-lock:', err);
      } finally {
        setCheckingLocation(false);
      }
    };

    checkGeoLock();
  }, [enrichedJob]);

  // Add keyboard shortcut (ESC) to go back
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        history.goBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [history]);

  // Handle geo-lock dialog close
  const handleGeoLockDialogClose = () => {
    setGeoLockDialogOpen(false);
    history.goBack();
  };

  // Show error if job fetch failed
  if (error) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h4" color="error" gutterBottom>
          Job Not Found
        </Typography>
        <Typography variant="body1" color="textSecondary">
          The job you're looking for doesn't exist or has been removed.
        </Typography>
      </Box>
    );
  }

  // Show loading while fetching
  if (isLoading || !enrichedJob || checkingLocation) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="h6" color="textSecondary">
          {checkingLocation ? 'Verifying location access...' : 'Loading job details...'}
        </Typography>
      </Box>
    );
  }

  // Extract data from enriched job result
  const job = enrichedJob.job;
  const company = enrichedJob.company || { name: 'Company', id: null };
  const file = enrichedJob.file || { publicUrl: '' };
  const plan = enrichedJob.plan;

  // Check if job has geo-lock enabled
  const hasGeoLockPlan = () => {
    if (!plan || !plan.items) return false;
    
    const planItems = Array.isArray(plan.items) ? plan.items : [plan.items];
    const hasGeoLock = planItems.some(item => {
      if (typeof item === 'object' && (item.planId === PLAN_TYPE.GEO_LOCK || item.id === PLAN_TYPE.GEO_LOCK)) {
        return true;
      }
      return false;
    });

    // Also check metadata
    if (plan.metadata && plan.metadata.geoLockEnabled === true) {
      return true;
    }

    return hasGeoLock;
  };

  const isGeoLockedJob = hasGeoLockPlan();

  console.log('🔍 [JobViewPublic] job id:', job?.id);
  console.log('🔍 [JobViewPublic] company id:', company?.id);
  console.log('🔍 [JobViewPublic] file id:', file?.id);
  console.log('🔍 [JobViewPublic] job category:', job?.category);
  console.log('🔍 [JobViewPublic] is geo-locked:', isGeoLockedJob);

  // Ensure we have a valid job
  if (!job || !job.id) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h4" color="error" gutterBottom>
          Job Not Found
        </Typography>
        <Typography variant="body1" color="textSecondary">
          The job you're looking for doesn't exist or has been removed.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Geo-lock dialog */}
      <GeoLockDialog
        open={geoLockDialogOpen}
        onClose={handleGeoLockDialogClose}
        allowedRegions={job.regionalRestrictions}
        userLocation={userLocation}
      />

      {/* Only show job content if not geo-blocked */}
      {!isGeoBlocked && (
        <>
          <JobGeneralPublicView
            headerSection={
              <Tooltip title="Or press ESC to go back" arrow>
                <Button
                  className={classes.backButton}
                  startIcon={<ArrowBackIcon />}
                  onClick={() => history.goBack()}>
                  Back
                </Button>
              </Tooltip>
            }
            companySection={
              <CompanyView company={company} logoUrl={file.publicUrl || ''} />
            }
            jobSection={<JobView job={job} isPreview={false} />}
            sidebarSection={
              <>
                <JobSidebar job={job} isGeoLocked={isGeoLockedJob} />
                <CompanySidebarInfo company={company} logoUrl={file.publicUrl || ''} />
              </>
            }
          />
          
          {/* Similar Jobs Section */}
          <SimilarJobs currentJobId={job.id} category={job.category} maxJobs={6} />
          
          {/* Bottom Navigation - Easy access to go back */}
          <Box className={classes.bottomNavigation}>
            <Typography variant="body1" className={classes.bottomNavigationText}>
              Found what you're looking for?
            </Typography>
            <Tooltip title="Or press ESC to go back" arrow>
              <Button
                className={classes.backButtonBottom}
                startIcon={<ArrowBackIcon />}
                onClick={() => history.goBack()}
                size="large">
                Back to Browse More Jobs
              </Button>
            </Tooltip>
            <Typography variant="caption" color="textSecondary">
              or press ESC to go back
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};

export default JobViewPublic;
